import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, CheckCircle2, ArrowUpRight } from 'lucide-react';
import { RevealSection, RevealCard, staggerContainer } from '../components/Animations';

import { serviceData } from '../data/serviceData';
import SEO from '../components/SEO';
import { blogPosts } from '../data/blogData';

const ServicePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const service = serviceData[id as keyof typeof serviceData];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!service) navigate('/');
  }, [service, navigate]);

  if (!service) return null;

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: service.title,
    provider: {
      '@type': 'ProfessionalService',
      name: 'Emerge Automations',
    },
    description: service.description,
    areaServed: ['UK', 'USA'],
  };

  return (
    <div className="relative">
      <SEO
        title={service.seoTitle || `${service.title} | Emerge Automations`}
        description={service.seoDescription || service.description}
        canonical={`https://emergeautomations.com/services/${id}/`}
        schemaData={serviceSchema}
        keywords={service.keywords}
        faq={service.faq}
        howTo={service.howTo}
        breadcrumbs={[
          { name: 'Home', item: 'https://emergeautomations.com' },
          { name: 'Services', item: 'https://emergeautomations.com/#services' },
          { name: service.title, item: `https://emergeautomations.com/services/${id}` },
        ]}
      />

      <div className="pt-32 md:pt-40 pb-24">
        <div className="container-custom">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
            {/* Breadcrumb */}
            <Link
              to="/#services"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-white transition-colors mb-10"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
              Back to services
            </Link>

            {/* Header */}
            <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
              <div className="lg:col-span-8">
                <span className="eyebrow mb-6 inline-block">Service</span>
                <div className="mb-8 h-px bg-white/10" />
                <div className="mb-8 w-12 h-12 rounded-[var(--radius-md)] border border-white/10 bg-white/[0.02] flex items-center justify-center">
                  {React.cloneElement(service.icon as React.ReactElement<any>, {
                    className: 'w-6 h-6 text-[color:var(--color-accent)]',
                    strokeWidth: 1.5,
                  })}
                </div>
                <RevealSection>
                  <h1 className="text-[clamp(2.25rem,5vw,4rem)] font-display font-semibold text-white leading-[1.05] tracking-[-0.035em] mb-6">
                    {service.title}
                  </h1>
                </RevealSection>
                <p className="text-lg md:text-xl text-zinc-400 leading-[1.6] max-w-2xl">
                  {service.description}
                </p>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-8 space-y-14">
                <RevealSection>
                  <h2 className="text-sm font-medium text-white mb-5 flex items-center gap-3">
                    <span className="w-1 h-1 rounded-full bg-[color:var(--color-accent)]" />
                    <span className="eyebrow">Comprehensive Overview</span>
                  </h2>
                  <div className="p-6 md:p-8 rounded-[var(--radius-lg)] border border-white/10 bg-[color:var(--color-surface-1)] text-[16px] text-zinc-300 leading-relaxed">
                    {service.comprehensive}
                  </div>
                </RevealSection>

                {service.longContent && (
                  <RevealSection>
                    <div
                      className="prose-emerge max-w-none text-[16px] leading-[1.75]
                        [&_h2]:text-2xl md:[&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:mt-14 [&_h2]:mb-5 [&_h2]:text-white
                        [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-10 [&_h3]:mb-4 [&_h3]:text-white
                        [&_p]:mb-5
                        [&_ul]:mb-5 [&_ul]:pl-5 [&_ul]:list-disc
                        [&_li]:mb-2"
                      dangerouslySetInnerHTML={{ __html: service.longContent }}
                    />
                  </RevealSection>
                )}

                <RevealSection>
                  <h2 className="text-sm font-medium text-white mb-5 flex items-center gap-3">
                    <span className="w-1 h-1 rounded-full bg-[color:var(--color-accent)]" />
                    <span className="eyebrow">Key Benefits</span>
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {service.benefits.map((benefit, i) => (
                      <RevealCard
                        key={i}
                        className="p-5 rounded-[var(--radius-md)] border border-white/10 bg-[color:var(--color-surface-1)] flex items-start gap-3"
                      >
                        <CheckCircle2
                          className="w-5 h-5 text-[color:var(--color-accent)] shrink-0 mt-0.5"
                          strokeWidth={1.75}
                        />
                        <p className="text-[15px] text-zinc-300 leading-relaxed">{benefit}</p>
                      </RevealCard>
                    ))}
                  </div>
                </RevealSection>

                <RevealSection>
                  <h2 className="text-sm font-medium text-white mb-5 flex items-center gap-3">
                    <span className="w-1 h-1 rounded-full bg-[color:var(--color-accent)]" />
                    <span className="eyebrow">Use Cases</span>
                  </h2>
                  <div className="border-t border-white/10">
                    {service.useCases.map((useCase, i) => (
                      <RevealCard
                        key={i}
                        className="flex items-center justify-between gap-4 py-5 border-b border-white/10 group"
                      >
                        <div className="flex items-center gap-4">
                          <span
                            data-mono
                            className="text-[11px] uppercase tracking-[0.12em] text-zinc-500 w-8"
                          >
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="text-[15px] text-zinc-300">{useCase}</span>
                        </div>
                        <ArrowUpRight
                          className="w-4 h-4 text-zinc-600 group-hover:text-[color:var(--color-accent)] transition-colors"
                          strokeWidth={1.75}
                        />
                      </RevealCard>
                    ))}
                  </div>
                </RevealSection>
              </div>

              {/* Sticky CTA */}
              <aside className="lg:col-span-4">
                <RevealCard className="sticky top-28 p-6 md:p-8 rounded-[var(--radius-xl)] bg-[color:var(--color-surface-1)] border border-white/10">
                  <span className="live-pill mb-5">
                    <span className="live-pill__dot" />
                    <span>Free · 15 min</span>
                  </span>
                  <h3 className="text-xl font-semibold text-white leading-[1.2] tracking-[-0.015em] mb-3">
                    Ready to implement {service.title}?
                  </h3>
                  <p className="text-[15px] text-zinc-400 leading-relaxed mb-6">
                    Book a free 15-minute discovery call to see how this service can transform your business operations.
                  </p>
                  <Link to="/#calendly-booking" className="btn-primary w-full justify-center">
                    Schedule consultation
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                  <p className="text-xs text-center text-zinc-500 mt-4">
                    No commitment. Expert advice only.
                  </p>
                </RevealCard>
              </aside>
            </div>

            {/* Related Insights */}
            <RevealSection className="mt-24 md:mt-32">
              <div className="flex items-end justify-between gap-6 mb-10">
                <div>
                  <span className="eyebrow">Related insights</span>
                  <div className="mt-3 mb-5 h-px bg-white/10 w-full" />
                  <h2 className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[-0.02em]">
                    Further reading
                  </h2>
                </div>
                <Link
                  to="/blog/"
                  className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-white hover:text-[color:var(--color-accent)] transition-colors"
                >
                  View all
                  <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                </Link>
              </div>

              <motion.div
                variants={staggerContainer}
                initial="initial"
                whileInView="whileInView"
                viewport={{ once: true, margin: '-100px' }}
                className="grid md:grid-cols-3 gap-4"
              >
                {blogPosts.slice(0, 3).map((post) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.id}/`}
                    className="group block"
                    aria-label={`Read more about ${post.title}`}
                  >
                    <RevealCard className="h-full rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors overflow-hidden flex flex-col">
                      <div className="aspect-[16/10] overflow-hidden border-b border-white/5">
                        <img
                          src={post.image}
                          alt={`Emerge Automations Insight: ${post.title} - AI Strategy and Implementation`}
                          className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500"
                          width="400"
                          height="225"
                          referrerPolicy="no-referrer"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      <div className="p-5 flex flex-col flex-grow">
                        <h3 className="text-[15px] font-semibold text-white leading-[1.35] line-clamp-2 mb-3 group-hover:text-[color:var(--color-accent)] transition-colors">
                          {post.title}
                        </h3>
                        <p className="text-[13px] text-zinc-400 leading-relaxed mb-4 line-clamp-3">
                          {post.excerpt}
                        </p>
                        <div className="mt-auto flex items-center justify-between text-sm text-white pt-4 border-t border-white/5">
                          <span className="font-medium">Read post</span>
                          <ArrowUpRight
                            className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] transition-colors"
                            strokeWidth={1.75}
                          />
                        </div>
                      </div>
                    </RevealCard>
                  </Link>
                ))}
              </motion.div>
            </RevealSection>

            {/* Related Services */}
            <RevealSection className="mt-24 md:mt-32">
              <div className="mb-10">
                <span className="eyebrow">Related services</span>
                <div className="mt-3 mb-5 h-px bg-white/10 w-full" />
                <h2 className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[-0.02em]">
                  Explore more
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {service.relatedServices.map((relatedId: string) => {
                  const related = serviceData[relatedId as keyof typeof serviceData];
                  return (
                    <Link
                      key={relatedId}
                      to={`/services/${relatedId}/`}
                      className="group block"
                      aria-label={`Explore our ${related.title} services`}
                    >
                      <RevealCard className="h-full p-6 rounded-[var(--radius-lg)] bg-[color:var(--color-surface-1)] border border-white/10 hover:border-white/25 transition-colors">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="w-10 h-10 rounded-[var(--radius-sm)] border border-white/10 bg-white/[0.02] flex items-center justify-center shrink-0">
                            {React.cloneElement(related.icon as React.ReactElement<any>, {
                              className: 'w-5 h-5 text-[color:var(--color-accent)]',
                              strokeWidth: 1.5,
                            })}
                          </div>
                          <h3 className="text-lg font-semibold text-white tracking-[-0.01em] group-hover:text-[color:var(--color-accent)] transition-colors">
                            {related.title}
                          </h3>
                        </div>
                        <p className="text-[14px] text-zinc-400 leading-relaxed mb-5">
                          {related.description}
                        </p>
                        <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                          Learn more
                          <ArrowUpRight
                            className="w-4 h-4 text-zinc-500 group-hover:text-[color:var(--color-accent)] transition-colors"
                            strokeWidth={1.75}
                          />
                        </div>
                      </RevealCard>
                    </Link>
                  );
                })}
              </div>
            </RevealSection>

            {/* Final CTA */}
            <RevealSection className="mt-24 md:mt-32">
              <RevealCard className="relative p-10 md:p-16 rounded-[var(--radius-xl)] border border-white/10 bg-[color:var(--color-surface-1)] overflow-hidden text-center">
                <div
                  className="pointer-events-none absolute inset-0 opacity-60"
                  style={{
                    background:
                      'radial-gradient(circle at 50% 120%, rgba(198,242,78,0.10), transparent 55%)',
                  }}
                />
                <div className="relative">
                  <span className="eyebrow eyebrow--accent mb-5 inline-block">
                    Ready to automate
                  </span>
                  <h2 className="text-3xl md:text-5xl font-display font-semibold text-white leading-[1.1] tracking-[-0.025em] mb-5">
                    Let's build your advantage.
                  </h2>
                  <p className="text-[16px] text-zinc-400 leading-relaxed max-w-xl mx-auto mb-8">
                    Join forward-thinking SMEs in the UK and USA who are scaling faster with our custom AI solutions.
                  </p>
                  <Link to="/#calendly-booking" className="btn-primary">
                    Book your free AI audit
                    <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
                  </Link>
                </div>
              </RevealCard>
            </RevealSection>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ServicePage;
